import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { WallboardViewService } from '../wallboard-view.service';
import { ThermometerModuleDataApiModel, WVCustom, WVMain } from '../wallboard-view.model';
import { KpiSettingsTransferObj, ModuleType } from '../../wallboards.model';

@Component({
    selector: 'cv-wallboard-view-custom-kpi',
    templateUrl: './wallboard-view-custom-kpi.component.html',
    styleUrls: ['./wallboard-view-custom-kpi.component.scss']
})
export class WallboardViewCustomKpiComponent implements OnInit, OnDestroy {
    @Input() index;
    @Input() height;
    @Input() width;
    @Input() firstSlide;

    public wvf: WVMain;
    public wvcf: WVCustom;
    public kpiState: KpiSettingsTransferObj = null;
    public kpiData: ThermometerModuleDataApiModel = null;
    public kpiMetricName = "";

    private DirectionUp: number = 1;
    public slideError: boolean = false;
    public verticalLayout = true;
    public titleFontSize: number | string;
    public valueFontSize: number | string;
    public vMetricFontSize: number | string;
    public hMetricFontSize: number | string;
    public metricHeight: number | string;
    public titleHeight: number | string;
    public valueHeight: number | string;
    public objectiveColor: string = "#a8afb9";

    private updateTimerRef: NodeJS.Timeout = null;
    private isDestroyed: boolean = false;
    private _hasBackdrop: boolean = false;
    private _moduleKey: string = "";

    get hasBackdrop(): boolean {
        return this._hasBackdrop;
    }

    get moduleKey(): string {
        return this._moduleKey;
    }

    constructor(
        private service: WallboardViewService
    ) { }

    ngOnInit(): void {
        this.wvf = this.service.WVMain;
        this.wvcf = this.service.WVCustom;

        this._hasBackdrop = this.wvcf?.Slide.HasBackdrop ?? false;
        this._moduleKey = this.wvcf?.Slide.WallboardModuleDetails[this.index].WallboardModuleKey ?? "";

        this.verticalLayout = Math.floor(this.height) * 2 >= Math.floor(this.width);
        let adjustedHeight = Math.floor(this.height) > Math.floor(this.width) ? Math.floor((this.width / 1.18) * 0.75) : this.height * 0.75;
        this.titleFontSize = Math.floor(adjustedHeight * 0.19);
        this.valueFontSize = Math.floor(adjustedHeight * 0.33);
        this.vMetricFontSize = Math.floor(adjustedHeight * 0.145);
        this.hMetricFontSize = Math.floor(adjustedHeight * 0.30);
        this.metricHeight = this.verticalLayout ? Math.floor((this.height * .25)) + "px" : Math.floor((this.height * .35)) + "px";
        this.titleHeight = Math.floor((this.height * .25)) + "px";
        this.valueHeight = this.verticalLayout ? Math.floor((this.height * .5)) + "px" : Math.floor((this.height * .35)) + "px";

        let moduleState = this.service.loadModuleState(ModuleType.Kpi, this.wvf.WallboardCreatorID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.kpiState = response.kpiObj;
        });

        let moduleData = this.service.getThermometerKpiModuleData(this.wvcf.Slide.WallboardID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.kpiData = response;
        });

        this.service.getMetricName(this.wvcf.Slide.WallboardID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.kpiMetricName = response;
        });

        Promise.all([moduleState, moduleData]).then(responses => {
            this.getNewObjectiveColorStyle();

            if (!this.firstSlide) {
                let dup = false;
                for (let i = 0; i < this.wvcf.ReadyModules.length; i++) {
                    if (this.moduleKey == this.wvcf.ReadyModules[i]) {
                        dup = true;
                        break;
                    }
                }

                if (!dup)
                    this.wvcf.ReadyModules.push(this.moduleKey);
            }

            if (!this.updateTimerRef) {
                const refreshInterval = this.kpiData.Has_Current_Metric
                    ? WallboardViewService.REALTIME_DATA_REFRESH_INTERVAL
                    : WallboardViewService.HISTORICAL_DATA_REFRESH_INTERVAL;
    
                this.updateTimerRef = setInterval(() => this.updateSlideData(), refreshInterval);
            }
        });
    }

    ngOnDestroy(): void {
        // End data updates
        if (this.updateTimerRef != null)
            clearInterval(this.updateTimerRef);

        this.isDestroyed = true;
    }

    getNewObjectiveColorStyle() {
        if (this.kpiState.isOverride) { // Old way of calculating color
            if (this.kpiData.Metric_Data.Direction == this.DirectionUp) {
                if (((this.kpiData.Metric_Data.MetricValue - this.kpiState.LowerBound) >= (this.kpiState.Value - this.kpiState.LowerBound))) {
                    this.objectiveColor = "#97c93c"; // green "Objective Green"
                    return;
                }
                else if (((this.kpiData.Metric_Data.MetricValue - this.kpiState.LowerBound) < (this.kpiState.Alert - this.kpiState.LowerBound))) {
                    this.objectiveColor = "#df1f26"; // red "Objective Red"
                    return;
                }
                else if (((this.kpiData.Metric_Data.MetricValue - this.kpiState.LowerBound) >= (this.kpiState.Alert - this.kpiState.LowerBound)
                    && (this.kpiData.Metric_Data.MetricValue - this.kpiState.LowerBound) < (this.kpiState.Value - this.kpiState.LowerBound))) {
                    this.objectiveColor = "#f0ce14"; // yellow "Objective Yellow"
                    return;
                }
            }
            else {
                if (((this.kpiData.Metric_Data.MetricValue - this.kpiState.LowerBound) <= (this.kpiState.Value - this.kpiState.LowerBound))) {
                    this.objectiveColor = "#97c93c"; // green "Objective Green"
                    return;
                }
                else if (((this.kpiData.Metric_Data.MetricValue - this.kpiState.LowerBound) > (this.kpiState.Alert - this.kpiState.LowerBound))) {
                    this.objectiveColor = "#df1f26"; // red "Objective Red"
                    return;
                }
                else if (((this.kpiData.Metric_Data.MetricValue - this.kpiState.LowerBound) <= (this.kpiState.Alert - this.kpiState.LowerBound)
                    && (this.kpiData.Metric_Data.MetricValue - this.kpiState.LowerBound) > (this.kpiState.Value - this.kpiState.LowerBound))) {
                    this.objectiveColor = "#f0ce14"; // yellow "Objective Yellow"
                    return;
                }
            }
        }
        else if (this.kpiData.Metric_Data.HasPO && this.kpiData.Metric_Data.objectiveData != null) { // New objectives without override
            // Use poValueColor, since the backend did the logic of calculating it
            this.objectiveColor = this.kpiData.Metric_Data.objectiveData.poValueColor;
            return;
        }

        this.objectiveColor = "#a8afb9"; // Default. Gray "Valyrian Steel"
    }

    async updateSlideData() {
        if (this.isDestroyed)
            return;

        let response = await this.service.getThermometerKpiModuleData(this.wvcf.Slide.WallboardID, this.moduleKey);

        if (this.isDestroyed)
            return;

        this.kpiData = response;
        this.getNewObjectiveColorStyle();
    }
}
